import { formatInTimeZone } from 'date-fns-tz';
import { convertToSeconds, formatTimestampInTimezone, oneHourSeconds } from 'utils/time';
import midnightOfDate from 'utils/midnightOfDate';
import { getDayString } from 'utils/getDayString';
import { Reel } from 'types/reelPlayer';
import { RewindPlaylists, SmartRewindPlaylistBlock } from 'utils/rewinds/playlistBlocks.types';
import { RewindRecording } from 'types/recordings';

export const transformHighlights = (
  highlights: RewindPlaylists | undefined,
  utcOffset: number,
  hasPermissions: boolean = true,
  timezone: string,
  prerollEnabled: boolean = false,
) => {
  const reelsLimit = 10;
  const filteredHighlightsReels: Reel[] = [];
  const todayMidnight = midnightOfDate(utcOffset);

  if (!highlights) return [];

  const sortedHighlightsArray = Array.from(highlights.values())
    .flatMap((item: SmartRewindPlaylistBlock) => item?.rewinds || [])
    .sort((a: RewindRecording, b: RewindRecording) => {
      const aTime = new Date(a.startDate).getTime();
      const bTime = new Date(b.startDate).getTime();
      return bTime - aTime;
    });

  for (let index = 0; index < sortedHighlightsArray.length; index++) {
    const rewind = sortedHighlightsArray[index];

    const clipUrl = rewind?.clips?.[0]?.url || rewind?.highlights?.url;
    if (!clipUrl || !rewind.endDate || !rewind.startDate) continue;

    const currentDate = new Date();
    const endDate = new Date(rewind?.endDate);
    const startDate = new Date(rewind?.startDate);

    const startTimestamp = convertToSeconds(startDate.getTime());
    const endTimestamp = convertToSeconds(endDate.getTime());
    const nowTimestamp = convertToSeconds(currentDate.getTime());
    const endDateHour = parseInt(formatTimestampInTimezone(endTimestamp, utcOffset, 'H'), 10);

    // Pre-roll drops the 1-hour publish buffer so today's clips show up immediately.
    if (!prerollEnabled && endTimestamp > nowTimestamp - oneHourSeconds) continue;

    const dayOfWeek = formatInTimeZone(startDate, timezone, 'EEEE');
    const day = getDayString(dayOfWeek, timezone, 'EEEE');
    const time = formatInTimeZone(startDate, timezone, 'h:mmaaa').toLowerCase();

    const isFromToday = endTimestamp >= todayMidnight;
    const highlightUrl = clipUrl;
    const reelsFromToday = filteredHighlightsReels.filter((d) => d.isFromToday === true);
    // Vacuously true on the first iteration (empty array); the dropYesterdayThreshold guard below prevents a premature break.
    const allReelsAreFromToday = reelsFromToday.length === filteredHighlightsReels.length;
    const difference = reelsLimit - reelsFromToday.length;

    let displayLabel = `${day}, ${time}`;

    if (!isFromToday) {
      if (endDateHour >= 0 && endDateHour < 12) {
        displayLabel = `${day} Morning`;
      } else if (endDateHour >= 12 && endDateHour <= 16) {
        displayLabel = `${day} Afternoon`;
      } else {
        displayLabel = `${day} Evening`;
      }
    }

    const reelsWithSameDisplayLabel = filteredHighlightsReels.filter(
      (reel) => reel.displayLabel === displayLabel,
    );

    // Pre-roll drops yesterday as soon as today has any segment (threshold 5 → 1).
    const dropYesterdayThreshold = prerollEnabled ? 1 : 5;
    if (
      !isFromToday &&
      filteredHighlightsReels.length >= dropYesterdayThreshold &&
      allReelsAreFromToday
    )
      break;

    // If we have at least 10 highlights then thats all we need, see ya layta playa.
    if (filteredHighlightsReels.length >= reelsLimit) break;

    // If we have x reels from a particular time of day already then thats all we need, adios muchacho.
    if (!isFromToday && reelsWithSameDisplayLabel.length > Math.floor(reelsLimit / difference))
      continue;

    filteredHighlightsReels.push({
      startTimestamp,
      highlightUrl,
      displayLabel,
      isFromToday,
    });
  }

  // Pre-roll replaces the rolling-unlock paywall when there's a today segment.
  const hasTodaySegment = filteredHighlightsReels.some((reel) => reel.isFromToday);
  const skipPaywall = prerollEnabled && hasTodaySegment;

  if (skipPaywall) {
    // Today reels are free here but must still count toward the every-2-clips midroll cadence.
    for (const reel of filteredHighlightsReels) {
      if (reel.isFromToday) {
        reel.shouldCountAsClipPlayed = true;
      }
    }
  }

  // Apply rolling unlock paywall logic
  if (!hasPermissions && !skipPaywall) {
    const todayReels = filteredHighlightsReels.filter((reel) => reel.isFromToday);
    const todayCount = todayReels.length;

    // Reels are currently in newest-to-oldest order (before reverse)
    // For rolling unlock: newest 2 are paywalled when 3+, newest 1 when 1-2
    const paywallCount = todayCount === 0 ? 0 : todayCount >= 3 ? 2 : 1;

    let todayReelsProcessed = 0;
    for (let i = 0; i < filteredHighlightsReels.length; i++) {
      const reel = filteredHighlightsReels[i];
      if (reel.isFromToday) {
        todayReelsProcessed++;
        // Paywall the first N today reels (newest, since we're in newest-first order)
        if (todayReelsProcessed <= paywallCount) {
          reel.highlightUrl = 'paywalled-highlight-reel';
        } else if (todayCount >= 5) {
          // Set shouldCountAsClipPlayed for free today clips when 5+ today clips
          reel.shouldCountAsClipPlayed = true;
        }
      }
    }
  }

  return filteredHighlightsReels.reverse();
};

export default transformHighlights;
